import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { FeaturedVideosSection } from '../components/featured/FeaturedVideosSection';
import { Play, ExternalLink, Youtube, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  'All',
  'Bible Stories',
  'Bible Teachings',
  "Gen Z: God's Emerging Warriors",
  'Teacher Resources',
];

const Videos = () => {
  const [videos, setVideos] = useState([]);
  const [socialInfo, setSocialInfo] = useState({});
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [loading, setLoading] = useState(true);
  const [activeVideo, setActiveVideo] = useState(null);

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/social-videos`);
      setVideos(data.videos || []);
      setSocialInfo(data);
    } catch (error) {
      console.error('Failed to fetch videos:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredVideos = selectedCategory === 'All' 
    ? videos 
    : videos.filter(v => v.category === selectedCategory);

  const openVideo = (video) => {
    setActiveVideo(video);
    // Scroll to top of video
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-background-primary">
      <SEO 
        title="Videos - Bible Stories & Teachings | RestoreMyWord"
        description="Watch biblical videos, stories, teachings, and Gen Z content. Follow us on YouTube (@darelldyal) and TikTok (@darelldyal0) for the latest content."
        url="https://restoremyword.net/videos"
      />
      <Navbar />

      {/* Banner */}
      <section 
        className="relative py-16 md:py-20"
        style={{
          backgroundImage: 'url(https://images.pexels.com/photos/13036981/pexels-photo-13036981.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
        data-testid="videos-banner"
      >
        <div className="absolute inset-0 bg-brand-primary/85"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center">
          <Play className="h-14 w-14 text-brand-accent mx-auto mb-4" />
          <h1 className="font-heading text-4xl md:text-5xl font-semibold tracking-tighter leading-tight text-white mb-4" data-testid="videos-title">
            Videos
          </h1>
          <p className="font-body text-lg text-white/90 max-w-2xl mx-auto mb-6">
            Watch our latest biblical teachings, stories, and content directly here or on our social channels
          </p>

          {/* Follow Buttons */}
          <div className="flex flex-wrap gap-3 justify-center">
            <a
              href={socialInfo.youtube_url || 'https://youtube.com/@darelldyal'}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 px-5 py-2.5 bg-red-600 text-white font-body font-medium text-sm rounded-full hover:bg-red-700 transition-all duration-300"
              data-testid="follow-youtube-button"
            >
              <Youtube className="h-4 w-4" />
              <span>Subscribe on YouTube</span>
            </a>
            <a
              href={socialInfo.tiktok_url || 'https://www.tiktok.com/@darelldyal0'}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 px-5 py-2.5 bg-black text-white font-body font-medium text-sm rounded-full hover:bg-slate-800 transition-all duration-300"
              data-testid="follow-tiktok-button"
            >
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
              </svg>
              <span>Follow on TikTok</span>
            </a>
          </div>
        </div>
      </section>

      <FeaturedVideosSection />

      {/* Featured Video Player */}
      {activeVideo && (
        <section className="py-8 bg-slate-900" data-testid="featured-video-section">
          <div className="max-w-5xl mx-auto px-6 md:px-12">
            <div className="aspect-video bg-black rounded overflow-hidden shadow-lg">
              {activeVideo.platform === 'youtube' ? (
                <iframe
                  key={activeVideo.video_id}
                  className="w-full h-full"
                  src={`${activeVideo.embed_url}?autoplay=1&rel=0`}
                  title={activeVideo.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  data-testid="active-youtube-player"
                ></iframe>
              ) : (
                <iframe
                  key={activeVideo.video_id}
                  className="w-full h-full"
                  src={`https://www.tiktok.com/embed/v2/${activeVideo.video_id}`}
                  title={activeVideo.title}
                  frameBorder="0"
                  allow="encrypted-media"
                  allowFullScreen
                  data-testid="active-tiktok-player"
                ></iframe>
              )}
            </div>
            <div className="mt-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div>
                <h2 className="font-heading text-xl md:text-2xl font-medium text-white">
                  {activeVideo.title}
                </h2>
                {activeVideo.category && (
                  <span className="inline-block mt-2 bg-brand-accent/20 text-brand-accent text-xs font-medium uppercase tracking-wider rounded-full px-3 py-1">
                    {activeVideo.category}
                  </span>
                )}
              </div>
              <a
                href={activeVideo.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-2 px-4 py-2 bg-white text-brand-primary font-body font-medium text-sm rounded hover:bg-slate-100 transition-all duration-300 self-start"
                data-testid="watch-on-platform-button"
              >
                <ExternalLink className="h-4 w-4" />
                <span>Watch on {activeVideo.platform === 'youtube' ? 'YouTube' : 'TikTok'}</span>
              </a>
            </div>
          </div>
        </section>
      )}

      {/* Category Tabs */}
      <section className="py-8 bg-white border-b border-slate-200 sticky top-20 z-30" data-testid="video-categories">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-wrap gap-2 md:gap-3 justify-center">
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-4 py-2 rounded-full font-body font-medium text-sm transition-all duration-300 whitespace-nowrap ${
                  selectedCategory === cat
                    ? 'bg-brand-primary text-white'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
                data-testid={`category-${cat.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Videos Grid */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {loading ? (
            <div className="flex justify-center py-16">
              <RefreshCw className="h-8 w-8 text-brand-accent animate-spin" />
            </div>
          ) : filteredVideos.length === 0 ? (
            <div className="text-center py-16">
              <p className="font-body text-slate-600 mb-4">
                No videos in this category yet.
              </p>
              <a
                href={socialInfo.youtube_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-2 text-brand-accent hover:text-brand-accent-hover font-body font-medium"
              >
                <Youtube className="h-4 w-4" />
                <span>Visit our YouTube channel</span>
              </a>
            </div>
          ) : (
            <>
              <div className="mb-6 flex justify-between items-center">
                <p className="font-body text-sm text-slate-600">
                  Showing {filteredVideos.length} {filteredVideos.length === 1 ? 'video' : 'videos'}
                  {selectedCategory !== 'All' && ` in ${selectedCategory}`}
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="videos-grid">
                {filteredVideos.map((video) => (
                  <div 
                    key={`${video.platform}-${video.video_id}`}
                    onClick={() => openVideo(video)}
                    className="bg-white border border-slate-200 rounded shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300 overflow-hidden group cursor-pointer"
                    data-testid={`video-card-${video.video_id}`}
                  >
                    {/* Thumbnail */}
                    <div className="relative aspect-video bg-slate-100 overflow-hidden">
                      {video.thumbnail ? (
                        <img
                          src={video.thumbnail}
                          alt={video.title}
                          loading="lazy"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-brand-primary to-slate-700">
                          {video.platform === 'youtube' ? (
                            <Youtube className="h-16 w-16 text-white/50" />
                          ) : (
                            <Play className="h-16 w-16 text-white/50" />
                          )}
                        </div>
                      )}
                      {/* Play overlay */}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-300 flex items-center justify-center">
                        <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <Play className="h-6 w-6 text-brand-primary ml-1" fill="currentColor" />
                        </div>
                      </div>
                      {/* Platform badge */}
                      <div className="absolute top-2 right-2">
                        <span className={`inline-flex items-center space-x-1 rounded-full px-2 py-1 text-xs font-medium ${
                          video.platform === 'youtube' 
                            ? 'bg-red-600 text-white' 
                            : 'bg-black text-white'
                        }`}>
                          {video.platform === 'youtube' ? (
                            <><Youtube className="h-3 w-3" /><span>YouTube</span></>
                          ) : (
                            <span>TikTok</span>
                          )}
                        </span>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-5">
                      <span className="inline-block bg-brand-accent/10 text-brand-accent border border-brand-accent/20 rounded-full px-2.5 py-0.5 text-xs font-medium mb-3">
                        {video.category}
                      </span>
                      <h3 className="font-heading font-medium text-base md:text-lg text-brand-primary mb-2 line-clamp-2">
                        {video.title}
                      </h3>
                      {video.description && (
                        <p className="font-body text-sm text-slate-600 line-clamp-2">
                          {video.description}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-primary" data-testid="videos-cta">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-medium tracking-tight text-white mb-4">
            Never Miss a Video
          </h2>
          <p className="font-body text-white/80 mb-8">
            Subscribe and follow to get notified when we post new content
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <a
              href={socialInfo.youtube_url || 'https://youtube.com/@darelldyal'}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 px-6 py-3 bg-red-600 text-white font-body font-medium rounded hover:bg-red-700 transition-all duration-300"
            >
              <Youtube className="h-5 w-5" />
              <span>Subscribe on YouTube</span>
            </a>
            <a
              href={socialInfo.tiktok_url || 'https://www.tiktok.com/@darelldyal0'}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 px-6 py-3 bg-white text-brand-primary font-body font-medium rounded hover:bg-slate-100 transition-all duration-300"
            >
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
              </svg>
              <span>Follow on TikTok</span>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Videos;
